import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Eraser, Upload } from "lucide-react";

export default function SignaturePad({ onChange, role = "seller" }) {
  const padRef = useRef(null);
  const [typed, setTyped] = useState("");
  const [uploaded, setUploaded] = useState(null);
  const [tab, setTab] = useState("draw");

  useEffect(() => {
    if (tab === "draw") {
      onChange?.(null);
      return;
    }
    if (tab === "type" && typed.trim()) {
      onChange?.({ type: "type", name: typed.trim(), data_url: "" });
    }
    if (tab === "upload" && uploaded) {
      onChange?.({ type: "upload", name: typed || "", data_url: uploaded });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, typed, uploaded]);

  const handleDrawEnd = () => {
    if (padRef.current && !padRef.current.isEmpty()) {
      const dataUrl = padRef.current.getCanvas().toDataURL("image/png");
      onChange?.({ type: "draw", name: typed || "", data_url: dataUrl });
    } else {
      onChange?.(null);
    }
  };

  const handleClear = () => {
    padRef.current?.clear();
    onChange?.(null);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUploaded(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid={`signature-pad-${role}`} className="space-y-3">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="draw" data-testid={`sig-tab-draw-${role}`}>Draw</TabsTrigger>
          <TabsTrigger value="type" data-testid={`sig-tab-type-${role}`}>Type</TabsTrigger>
          <TabsTrigger value="upload" data-testid={`sig-tab-upload-${role}`}>Upload</TabsTrigger>
        </TabsList>

        <TabsContent value="draw">
          <div className="border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 h-48 relative overflow-hidden">
            <SignatureCanvas
              ref={padRef}
              penColor="#111827"
              canvasProps={{ className: "sig-canvas", "data-testid": `sig-canvas-${role}` }}
              onEnd={handleDrawEnd}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={`sig-clear-${role}`}
              className="absolute bottom-3 right-3"
              onClick={handleClear}
            >
              <Eraser size={14} className="mr-1" /> Clear
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input
              placeholder="Print full legal name"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              data-testid={`sig-name-${role}`}
            />
          </div>
        </TabsContent>

        <TabsContent value="type">
          <div className="border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 h-48 grid place-items-center">
            <div className="font-['Caveat'] text-5xl text-gray-900" style={{ fontFamily: 'cursive' }} data-testid={`sig-typed-preview-${role}`}>
              {typed || "Your signature"}
            </div>
          </div>
          <Input
            placeholder="Type your full legal name"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            data-testid={`sig-type-input-${role}`}
          />
        </TabsContent>

        <TabsContent value="upload">
          <div className="border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 h-48 grid place-items-center relative overflow-hidden">
            {uploaded ? (
              <img src={uploaded} alt="signature" className="max-h-full max-w-full" />
            ) : (
              <label className="cursor-pointer text-center text-sm text-gray-500">
                <Upload className="mx-auto mb-2" size={20} />
                <span>Click to upload signature image (PNG/JPG)</span>
                <input type="file" className="hidden" accept="image/*" onChange={handleUpload} data-testid={`sig-upload-${role}`} />
              </label>
            )}
          </div>
          <Input
            placeholder="Print full legal name"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            data-testid={`sig-upload-name-${role}`}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
